import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { LabelsService } from '@/api';
import { toast } from 'sonner';
import { t } from '@lingui/macro';
import type { 
  labels_CreateLabelDefinitionRequest,
  labels_LabelDefinitionResponse,
} from '@/api';
import type { UpdateLabelRequest } from '@/types';
import { apiErrorMessage } from './apiError';

// Query keys
const labelsKeys = {
  all: ['labels'] as const,
  lists: () => [...labelsKeys.all, 'list'] as const,
  list: (entityType: string, orgId: string) => [...labelsKeys.lists(), entityType, orgId] as const,
  detail: (id: string, orgId: string) => [...labelsKeys.all, 'detail', id, orgId] as const,
  usage: (labelId: string) => [...labelsKeys.all, 'usage', labelId] as const,
};

// List label definitions
export function useLabels(orgId: string, entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts' = 'all') {
  return useQuery({
    queryKey: labelsKeys.list(entityType, orgId),
    queryFn: () => LabelsService.getApiV1Labels1(entityType),
    select: (data) => data || [],
  });
}

// Get label by ID
export function useLabel(entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts', id: string, orgId: string, enabled = true) {
  return useQuery({
    queryKey: labelsKeys.detail(id, orgId),
    queryFn: () => LabelsService.getApiV1Labels2(entityType, id),
    enabled,
  });
}

// Create label definition
export function useCreateLabel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityType, data }: { entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts'; data: labels_CreateLabelDefinitionRequest }) =>
      LabelsService.postApiV1Labels1(entityType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: labelsKeys.all });
      toast.success(t`settings.labels.success.created`);
    },
    onError: (error: unknown) => {
      const errorMessage =
        apiErrorMessage(error) ||
        t({ id: 'settings.labels.error.create', message: 'Failed to create label' });
      toast.error(errorMessage);
    },
  });
}

// Update label definition
export function useUpdateLabel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityType, id, data }: { entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts'; id: string; data: UpdateLabelRequest }) =>
      LabelsService.putApiV1Labels1(entityType, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: labelsKeys.all });
      toast.success(t`settings.labels.success.updated`);
    },
    onError: (error: unknown) => {
      const errorMessage =
        apiErrorMessage(error) ||
        t({ id: 'settings.labels.error.update', message: 'Failed to update label' });
      toast.error(errorMessage);
    },
  });
}

// Delete label definition
export function useDeleteLabel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityType, id }: { entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts'; id: string }) =>
      LabelsService.deleteApiV1Labels1(entityType, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: labelsKeys.all });
      toast.success(t`settings.labels.success.deleted`);
    },
    onError: (error: unknown) => {
      const errorMessage =
        apiErrorMessage(error) ||
        t({ id: 'settings.labels.error.delete', message: 'Failed to delete label' });
      toast.error(errorMessage);
    },
  });
}

// Entity labels query keys (for labels assigned to specific entities)
const entityLabelsKeys = {
  all: ['entity-labels'] as const,
  entity: (entityType: string, entityId: string) => [...entityLabelsKeys.all, entityType, entityId] as const,
};

/**
 * Batch-fetch labels for multiple entities of the same type.
 * Returns a map of entityId -> labels[].
 * Uses useQueries to issue parallel requests for each entity.
 */
export function useEntityLabelsMap(
  entityIds: string[],
  entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts',
) {
  const queries = useQueries({
    queries: entityIds.map((id) => ({
      queryKey: entityLabelsKeys.entity(entityType, id),
      queryFn: () => LabelsService.getApiV1Labels(entityType, id),
      staleTime: 5 * 60 * 1000, // 5 minutes - labels change infrequently
    })),
  });

  const labelsMap = new Map<string, labels_LabelDefinitionResponse[]>();
  const isLoading = queries.some((q) => q.isLoading);

  entityIds.forEach((id, index) => {
    const data = queries[index]?.data;
    if (data) {
      labelsMap.set(id, data);
    }
  });

  return { labelsMap, isLoading };
}

/**
 * Fetch labels for a single entity.
 * Convenience wrapper around the labels API for components that display one entity.
 */
export function useEntityLabels(
  entityId: string | undefined,
  entityType: 'all' | 'groups' | 'children' | 'employees' | 'contacts' | 'contracts',
) {
  return useQuery({
    queryKey: entityLabelsKeys.entity(entityType, entityId ?? ''),
    queryFn: () => LabelsService.getApiV1Labels(entityType, entityId!),
    enabled: !!entityId,
    staleTime: 5 * 60 * 1000,
  });
}

// Get label usage count across all entities
export function useLabelUsage(labels: labels_LabelDefinitionResponse[]) {
  // This is a simplified version - in a real implementation, 
  // you might want to fetch actual usage from the backend
  // For now, we'll return a map with usage counts
  const usageMap = new Map<string, number>();
  
  // Initialize all labels with 0 usage
  labels.forEach(label => {
    if (label.id) {
      usageMap.set(label.id, 0);
    }
  });
  
  return usageMap;
}